import { api, type ApiError } from "./client";
import type { ApiTaskStatus } from "./types";
import { useResource } from "./useResource";
import type { RealtimeEvent } from "../realtime/RealtimeClient";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// Agents a repository starts on a schedule (routines.md, api.md §Routines).

/** In the order the form offers them, the default first. */
export const ROUTINE_OVERLAPS = [
  "skip_if_open",
  "skip_if_working",
  "start_anyway",
] as const;

export type RoutineOverlap = (typeof ROUTINE_OVERLAPS)[number];

export type RoutinePauseReason = "failures" | "manual" | "owner_left";

export type RoutineRunReason =
  | "billing"
  | "failures"
  | "owner_left"
  | "previous_open"
  | "previous_working"
  | "repository_archived";

/** A started run has no reason and a skipped one always has, as the table holds them. */
export type ApiRoutineRun = (
  | { readonly outcome: "skipped"; readonly reason: RoutineRunReason }
  | { readonly outcome: "started"; readonly reason: null }
) & {
  readonly created_at: string;
  /** Billing's refusal, for a run it skipped. */
  readonly detail: string | null;
  readonly public_id: string;
  readonly scheduled_for: string;
  readonly task: {
    readonly display_id: string;
    readonly public_id: string;
    readonly status: ApiTaskStatus;
    readonly title: string;
  } | null;
  readonly trigger: "manual" | "schedule";
};

export type ApiRoutine = {
  readonly base_branch: string | null;
  readonly created_at: string;
  readonly effort: string | null;
  readonly last_run: ApiRoutineRun | null;
  readonly model: string | null;
  readonly name: string;
  /** Null while paused. */
  readonly next_run_at: string | null;
  readonly overlap: RoutineOverlap;
  readonly owner: {
    readonly display_name: string;
    readonly public_id: string;
  };
  readonly pause_reason: RoutinePauseReason | null;
  readonly paused: boolean;
  readonly prompt: string;
  readonly public_id: string;
  readonly repository_id: string;
  readonly schedule: string;
  readonly schedule_description: string;
  readonly timezone: string;
  readonly updated_at: string;
};

export type RoutineCreate = {
  readonly base_branch?: string;
  readonly effort?: string;
  readonly model?: string;
  readonly name: string;
  readonly overlap?: RoutineOverlap;
  readonly prompt: string;
  readonly schedule: string;
  readonly timezone?: string;
};

/** `null` clears `base_branch`, `model` and `effort` back to the repository's. */
export type RoutinePatch = {
  readonly base_branch?: string | null;
  readonly effort?: string | null;
  readonly model?: string | null;
  readonly name?: string;
  readonly overlap?: RoutineOverlap;
  readonly paused?: boolean;
  readonly prompt?: string;
  readonly schedule?: string;
  readonly timezone?: string;
};

/**
 * A routine as the approval card shows it, before or after the change
 * (chat.md §Routine approval card).
 */
export type RoutineSnapshot = {
  readonly name: string;
  readonly next_runs: ReadonlyArray<string>;
  readonly overlap: RoutineOverlap;
  readonly paused: boolean;
  readonly prompt: string;
  readonly schedule: string;
  readonly schedule_description: string;
  readonly timezone: string;
};

export type ApiRoutineChange = {
  readonly action: "create" | "delete" | "update";
  /** Null for a delete. */
  readonly after: RoutineSnapshot | null;
  /** Null for a create. */
  readonly before: RoutineSnapshot | null;
  /** What the proposal sets, keyed by field name. */
  readonly fields: Readonly<Record<string, unknown>>;
  readonly routine_id: string | null;
};

export const OVERLAP_LABEL: Record<RoutineOverlap, string> = {
  skip_if_open: "Skip while the last run is open",
  skip_if_working: "Skip only while it's working",
  start_anyway: "Start another anyway",
};

export const OVERLAP_HELP: Record<RoutineOverlap, string> = {
  skip_if_open:
    "A run waiting in review holds the next one back, so runs can't pile up.",
  skip_if_working: "A run waiting in review doesn't hold the next one back.",
  start_anyway: "Every due time starts a task.",
};

export const runReasonLabel = (
  run: Extract<ApiRoutineRun, { readonly outcome: "skipped" }>,
): string => {
  switch (run.reason) {
    case "billing":
      return run.detail
        ? `Skipped by billing: ${run.detail}`
        : "Skipped by billing";
    case "failures":
      return "Paused after three failed runs in a row";
    case "owner_left":
      return "Paused: the owner left the workspace";
    case "previous_open":
      return "Skipped: the last run was still open";
    case "previous_working":
      return "Skipped: the last run was still working";
    case "repository_archived":
      return "Skipped: the repository is archived";
  }
};

// `manual` says nothing "Paused" doesn't.
export const pauseLabel = (reason: RoutinePauseReason | null): string =>
  reason === "failures"
    ? "Paused — three failed runs in a row"
    : reason === "owner_left"
      ? "Paused — the owner left the workspace"
      : "Paused";

/**
 * Whether an event on the repo scope moves what a routines view shows: a
 * routine changed (`routineId` narrows that to one), the client resynced, or a
 * task the view lists moved to another status — the one thing on the page no
 * routine event announces. Every agent state flip carries the task's status,
 * changed or not, many times a turn; only a change is news here.
 */
const refreshes = (
  event: RealtimeEvent,
  shownTasks: ReadonlyArray<{
    readonly public_id: string;
    readonly status: string;
  } | null>,
  routineId?: string,
): boolean => {
  const ref = (event.ref as { public_id?: string } | undefined)?.public_id;
  switch (event.kind) {
    case "resync":
      return true;
    case "routine.changed":
      return routineId === undefined || ref === routineId;
    case "task.updated": {
      const status = (event.summary as { status?: unknown } | undefined)
        ?.status;
      return (
        status !== undefined &&
        shownTasks.some(
          (task) =>
            task !== null && task.public_id === ref && task.status !== status,
        )
      );
    }
    default:
      return false;
  }
};

export type UseRepoRoutines = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly routines: ReadonlyArray<ApiRoutine>;
};

export const useRepoRoutines = (repoId: string | null): UseRepoRoutines => {
  const { data, error, loading, refetch } = useResource<{
    routines: ReadonlyArray<ApiRoutine>;
  }>(repoId ? `/repositories/${repoId}/routines` : null);
  const routines = data?.routines ?? [];
  useRealtimeScope(repoId ? `repo:${repoId}` : null, (event) => {
    if (
      refreshes(
        event,
        routines.map((r) => r.last_run?.task ?? null),
      )
    ) {
      refetch();
    }
  });
  return { error, loading, refetch, routines };
};

export type UseRoutineRuns = {
  readonly loading: boolean;
  readonly runs: ReadonlyArray<ApiRoutineRun>;
};

/** Newest first. Runs have no scope of their own, so this listens on the repo's. */
export const useRoutineRuns = (
  repoId: string,
  routineId: string,
): UseRoutineRuns => {
  const { data, loading, refetch } = useResource<{
    runs: ReadonlyArray<ApiRoutineRun>;
  }>(`/routines/${routineId}/runs`);
  const runs = data?.runs ?? [];
  useRealtimeScope(`repo:${repoId}`, (event) => {
    if (
      refreshes(
        event,
        runs.map((run) => run.task),
        routineId,
      )
    ) {
      refetch();
    }
  });
  return { loading, runs };
};

export const createRoutine = (
  repoId: string,
  body: RoutineCreate,
): Promise<ApiRoutine> =>
  api
    .post<{ routine: ApiRoutine }>(`/repositories/${repoId}/routines`, body)
    .then((r) => r.routine);

export const updateRoutine = (
  id: string,
  patch: RoutinePatch,
): Promise<ApiRoutine> =>
  api
    .patch<{ routine: ApiRoutine }>(`/routines/${id}`, patch)
    .then((r) => r.routine);

export const deleteRoutine = (id: string): Promise<unknown> =>
  api.del(`/routines/${id}`);

/** Run now: ignores the pause and the overlap policy (routines.md §Run now). */
export const runRoutine = (id: string): Promise<ApiRoutineRun> =>
  api.post<{ run: ApiRoutineRun }>(`/routines/${id}/run`).then((r) => r.run);
